/*
 * This object has all of apcon.com's popular pages' links.
 * You should use this object when linking to internal pages across apcon.com instead of strings.
 * Using this object instead of strings ensures that any URL changes in the future can be easily managed.
 *
 *
 * GOOD:
 * import { Link } from 'gatsby';
 * import wwwApconCom from '../../www.apcon.com';
 * <Link to={wwwApconCom.hardware.intellaview.DEFAULT} />
 *
 *
 * BAD:
 * import { Link } from 'gatsby';
 * <Link to="/hardware/intellaview/" />
 */

const wwwApconCom = {
  homepage: '/',
  about: '/about',
  blog: '/blog',
  careers: '/careers',
  channelPartners: '/channel-partners',
  contactUs: '/contact-us',
  corporateResponsibility: '/corporate-responsibility',
  events: '/events',
  legacyProductsPolicy: '/legacy-products-policy',
  newsroom: '/newsroom',
  privacyPolicy: '/privacy-policy',
  scheduleDemo: '/schedule-demo',
  getQuote: '/get-quote',
  search: '/search',
  technologyPartners: '/technology-partners',
  terms: '/terms',
  getSupport: '/get-support',
  jobPostings: 'https://apcon.applytojob.com/apply',

  // THIS IS THE OLD STRUCTURE FOR THE INTENLLAVIEW PRODUCT SUITE. DO NO USE GOING FORWARD
  hardware: {
    intellaflexXr: {
      DEFAULT: '/hardware/intellaflex-xr',
      faqs: '/hardware/intellaflex-xr/faqs',
      blades: {
        e20: '/hardware/intellaflex-xr/blades/20-port-40g-packet-aggregator',
        e32: '/hardware/intellaflex-xr/blades/32-port-1g-10-40g-packet-aggregator',
        e36MultiFunction:
          '/hardware/intellaflex-xr/blades/36-port-1g-10g-multi-function-blade',
        e36Aggregator:
          '/hardware/intellaflex-xr/blades/36-port-1g-10g-aggregator',
        copperBypassTap: '/hardware/intellaflex-xr/blades/copper-bypass-tap',
        edgeswitch: '/hardware/intellaflex-xr/blades/edgeswitch',
        hyperengine: '/hardware/intellaflex-xr/blades/hyperengine',
        intellastore: '/hardware/intellaflex-xr/blades/intellastore',
        opticalBypassTap: '/hardware/intellaflex-xr/blades/optical-bypass-tap',
      },
    },

    // DO NOT USE THESE LINKS. THEY ARE FOR REFERENCE ONLY.
    intellaview: {
      DEFAULT: '/intellaview/intellaview-hardware',
      faqs: '/intellaview/intellaview-hardware/faqs',
      latest: '/intellaview/intellaview-hardware/latest-updates',
      blades: {
        e16: '/intellaview/intellaview-hardware/blades/16-port-100g-400g-multi-function-blade',
        e28: '/intellaview/intellaview-hardware/blades/28-port-100g-400g-multi-function-blade',
        e36: '/intellaview/intellaview-hardware/blades/36-port-40g-100g-multi-function-blade',
        e52: '/intellaview/intellaview-hardware/blades/52-port-40g-100g-multi-function-blade',
        bypassTap: '/intellaview/intellaview-hardware/blades/bypass-tap',
        edgeswitch: '/intellaview/intellaview-hardware/blades/edgeswitch',
        edgeswitch400g:
          '/intellaview/intellaview-hardware/blades/400g-edgeswitch',
        hyperengine: '/intellaview/intellaview-hardware/blades/hyperengine',
      },
    },
    networkTaps: {
      DEFAULT: '/hardware/network-taps',
      apconTap: '/hardware/network-taps/apcon-tap',
    },
  },

  // THIS IS THE OLD STRUCTURE FOR THE SOFTWARE PRODUCT SUITE. DO NO USE GOING FORWARD
  software: {
    intellacloud: '/software/intellacloud',
    intellatapVm: '/intellaview/intellatap-vm',
    intellaview: '/intellaview/intellaview-gui',
    titan: '/software/titan',
    enterprise: '/intellaview/intellaview-enterprise',
    webxr: '/software/web-xr',
    intellaviewMobile: {
      DEFAULT: '/intellaview/intellaview-mobile-app',
      appleStore:
        'https://apps.apple.com/us/app/intellaview-mobile/id1574728969',
      googleStore:
        'https://play.google.com/store/apps/details?id=com.apcon.intellaviewmobile',
    },
  },

  //THIS IS THE NEW STRUCTURE FOR THE INTENLLAVIEW PRODUCT SUITE.
  intellaview: {
    DEFAULT: '/intellaview',
    hardware: {
      DEFAULT: '/intellaview/intellaview-hardware',
      faqs: '/intellaview/intellaview-hardware/faqs',
      latest: '/intellaview/intellaview-hardware/latest-updates',
      blades: {
        DEFAULT: '/intellaview/intellaview-hardware/blades',
        e16: '/intellaview/intellaview-hardware/blades/16-port-100g-400g-multi-function-blade',
        e28: '/intellaview/intellaview-hardware/blades/28-port-100g-400g-multi-function-blade',
        e36: '/intellaview/intellaview-hardware/blades/36-port-40g-100g-multi-function-blade',
        e52: '/intellaview/intellaview-hardware/blades/52-port-40g-100g-multi-function-blade',
        bypassTap: '/intellaview/intellaview-hardware/blades/bypass-tap',
        edgeswitch: '/intellaview/intellaview-hardware/blades/edgeswitch',
        edgeswitch400g:
          '/intellaview/intellaview-hardware/blades/400g-edgeswitch',
        hyperengine: '/intellaview/intellaview-hardware/blades/hyperengine',
      },
    },
    gui: '/intellaview/intellaview-gui',
    intellatapVm: '/intellaview/intellatap-vm',
    intellaviewMobile: '/intellaview/intellaview-mobile-app',
    enterprise: '/intellaview/intellaview-enterprise',
    enterpriseLite: '/intellaview/intellaview-enterprise/enterprise-lite/',
  },

  social: {
    facebook: 'https://www.facebook.com/APCONInc',
    linkedin: 'https://www.linkedin.com/company/apcon',
    twitter: 'https://twitter.com/APCON',
    youtube: 'https://www.youtube.com/c/apcon',
  },

  solutions: {
    DEFAULT: '/solutions',
    aerospaceTransportation: '/solutions/aerospace-transportation',
    empowerMonitoring: '/solutions/empower-monitoring',
    energyIndustry: '/solutions/energy-industry',
    entertainmentTravel: '/solutions/entertainment-travel',
    finance: '/solutions/finance',
    government: '/solutions/government',
    healthcare: '/solutions/healthcare',
    stateLocalEducation: '/solutions/state-local-education',
    networkDataCompliance: '/solutions/network-data-compliance',
    networkMonitoring: '/solutions/network-monitoring',
    networkSecurity: '/solutions/network-security',
    networkVisibility: '/solutions/network-visibility',
    softwareServices: '/solutions/software-services',
  },

  technologies: {
    deduplication: '/technologies/deduplication',
    netflowGeneration: '/technologies/netflow-generation',
    networkLatencyMeasurement: '/technologies/network-latency-measurement',
    networkPacketFiltering: '/technologies/network-packet-filtering',
    packetSlicing: '/technologies/packet-slicing',
    protocolHeaderStripping: '/technologies/protocol-header-stripping',
    tunneling: '/technologies/tunneling',
    deepPacketInspection: '/technologies/deep-packet-inspection',
    applicationFiltering: '/technologies/application-filtering',
    patternMatching: '/technologies/pattern-matching',
    trafficShaping: '/technologies/traffic-shaping',
    distributedLoadBalance: '/technologies/distributed-load-balance',
    applicationAnalytics: '/technologies/application-analytics',
  },
};

export default wwwApconCom;
